import type {
	CoreApiError,
	CoreApiModel,
	CoreApiPaginatedResponse,
	UseInfiniteQueryResult,
	UseQueryResult,
} from '@lqa-frontend/api-services';
import type { AxiosError } from 'axios';
import { isArray } from 'lodash';
import React from 'react';
import { CoreApiErrorAlert } from './core-api-error-alert.tsx';
import { Skeleton } from '@lqa-frontend/ui-system';

export function QueryBoundary<T>({
	onError,
	onLoad,
	onEmpty,
	children,
	query,
	loadingOnRefetch,
	as,
	className,
}: QueryBoundaryProps<T>) {
	const Comp = as || React.Fragment;

	if (query.isLoading || (loadingOnRefetch && query.isFetching)) {
		return (
			<Comp {...(as && { className })}>
				{onLoad || <Skeleton className="size-full min-h-8 min-w-14" />}
			</Comp>
		);
	}

	if (query.isError) {
		return (
			<Comp {...(as && { className })}>
				{onError || (
					<CoreApiErrorAlert error={query.error as AxiosError<CoreApiError>} />
				)}
			</Comp>
		);
	}

	if (!query.data) {
		return null;
	}

	const total =
		(query.data as unknown as CoreApiPaginatedResponse<CoreApiModel>)?.total ||
		(
			query.data as unknown as {
				pages: CoreApiPaginatedResponse<CoreApiModel>[];
			}
		)?.pages?.[0]?.total ||
		0;

	if (
		onEmpty &&
		((total === 0 && !query.data) ||
			(isArray(query.data) && query.data.length === 0) ||
			// eslint-disable-next-line ts/ban-ts-comment
			// @ts-expect-error
			(isArray(query.data.results) && query.data.results.length === 0))
	) {
		return <Comp {...(as && { className })}>{onEmpty}</Comp>;
	}

	// defined data is passed as a parameter to the function
	return (
		<Comp {...(as && { className })}>
			{typeof children === 'function'
				? (children as (data: T) => React.ReactNode)(query.data)
				: children}
		</Comp>
	);
}

interface QueryBoundaryProps<T> {
	children: React.ReactNode | ((data: T) => React.ReactNode);
	query:
		| UseQueryResult<T, AxiosError<unknown>>
		| UseInfiniteQueryResult<T, AxiosError<unknown>>;
	onLoad?: React.ReactNode;
	onError?: React.ReactNode;
	onEmpty?: React.ReactNode;
	loadingOnRefetch?: boolean;
	as?: React.ElementType;
	className?: string;
}
